import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';
import WaveLoader from '../components/WaveLoader';

function InsightCard({ label, title, body }) {
  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '24px', padding: '24px', boxShadow: 'var(--shadow-md)',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#8b7355', marginBottom: '12px' }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-dark)', lineHeight: 1.3, marginBottom: '12px' }}>
        {title}
      </div>
      <p style={{ fontSize: '14px', lineHeight: 1.75, color: 'var(--text-light)', margin: 0 }}>{body}</p>
    </div>
  );
}

function KpiCard({ label, value, note, accent = 'blue' }) { // eslint-disable-line no-unused-vars
  const colors = { blue: '#1f5eff', green: '#1f7a3f', amber: '#c98b12', teal: '#0f766e', red: '#c2410c' };
  return (
    <div className="metric-card">
      <span className="metric-label">{label}</span>
      <div className="metric-value" style={{ color: colors[accent] || colors.blue }}>{value}</div>
      {note && <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>{note}</div>}
    </div>
  );
}

function pct(v) { return v == null ? '0%' : `${Math.round(Number(v) * 100)}%`; } // eslint-disable-line no-unused-vars
function num(v, d = 2) { return v == null ? '0.00' : Number(v).toFixed(d); }

function FieldStrategy({ filters, loading, setLoading }) {
  const [summary, setSummary] = useState({}); // eslint-disable-line no-unused-vars
  const [opportunities, setOpportunities] = useState([]);
  const [typeMix, setTypeMix] = useState([]);
  const [urgencyUnmet, setUrgencyUnmet] = useState([]);
  const [competitorVolume, setCompetitorVolume] = useState([]);
  const [opportunityDaily, setOpportunityDaily] = useState([]);
  const [expandedCompetitor, setExpandedCompetitor] = useState(null);
  const [competitorMentions, setCompetitorMentions] = useState({});
  const [competitorMentionsLoading, setCompetitorMentionsLoading] = useState({});
  const [expandedOpportunity, setExpandedOpportunity] = useState(null);
  const [opportunityMentions, setOpportunityMentions] = useState({});
  const [opportunityMentionsLoading, setOpportunityMentionsLoading] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });

    axios.get(`/api/sales-strategy?${params}`)
      .then(res => {
        if (!active) return;
        setSummary(res.data.summary || {});
        setOpportunities(res.data.opportunities || []);
        setTypeMix(res.data.typeMix || []);
        setUrgencyUnmet(res.data.urgencyUnmet || []);
        setCompetitorVolume(res.data.competitorVolume || []);
        setOpportunityDaily(res.data.opportunityDaily || []);
      })
      .catch(err => { if (active) console.error('Error loading Sales Strategy:', err); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, setLoading]);

  const topOpp = opportunities[0];
  const topCompetitor = competitorVolume[0];
  const topType = typeMix[0];

  // Opportunity scatter (urgency vs unmet, sized by volume)
  const oppScatterData = urgencyUnmet.length ? [{ // eslint-disable-line no-unused-vars
    x: urgencyUnmet.map(o => o.urgency_ratio || 0),
    y: urgencyUnmet.map(o => o.unmet_ratio || 0),
    text: urgencyUnmet.map(o => o.entity_name),
    mode: 'markers+text',
    type: 'scatter',
    textposition: 'top center',
    marker: {
      size: urgencyUnmet.map(o => Math.max(8, Math.min(32, (o.volume || 1) * 1.5))),
      color: urgencyUnmet.map(o => o.opp_score || 0),
      colorscale: 'Blues', showscale: true,
      colorbar: { title: 'Opp Score', thickness: 12 },
    },
  }] : [];

  return (
    <div>
      {/* Header */}
      <div className="module-header">
        <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
          Strategic Intelligence
        </div>
        <h2>Sales Strategy</h2>
        <p>Opportunity identification, strategic targeting, root cause, and competitive pressure analysis.</p>
      </div>

      {/* Filter Bar */}
      <FilterBar />

      {/* KPIs — hidden for now
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <KpiCard label="Opportunities" value={summary.total_opportunities ?? 0} note="Scored entity patterns" accent="blue" />
        <KpiCard label="Max Opp Score" value={num(summary.max_opp_score)} note="Highest opportunity score" accent="green" />
        <KpiCard label="Avg Urgency" value={pct(summary.avg_urgency_ratio)} note="Positive-intent rate" accent="amber" />
        <KpiCard label="Avg Unmet" value={pct(summary.avg_unmet_ratio)} note="No-appointment rate" accent="red" />
        <KpiCard label="Avg Pain" value={pct(summary.avg_pain_ratio)} note="Negative-sentiment rate" accent="teal" />
      </div>
      */}

      {/* Insight Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <InsightCard
          label="Top Opportunity"
          title={topOpp ? `${topOpp.entity_name} is the top opportunity.` : 'No opportunities detected yet.'}
          body={topOpp
            ? `${topOpp.entity_name} (${topOpp.entity_type}) was mentioned in ${topOpp.conversation_count} distinct conversations.`
            : 'Increase conversation volume to surface opportunity patterns.'}
        />
        <InsightCard
          label="Competitive Pressure"
          title={topCompetitor ? `${topCompetitor.competitor_name} is the most mentioned competitor.` : 'No competitor signals yet.'}
          body={topCompetitor
            ? `${topCompetitor.competitor_name} appears in ${topCompetitor.mention_count} conversations with an average sentiment of ${num(topCompetitor.avg_sentiment)}.`
            : 'No competitor entities have been detected in the current scope.'}
        />
        <InsightCard
          label="Opportunity Mix"
          title={topType ? `${topType.entity_type} entities hold the highest average score.` : 'No opportunity type data.'}
          body={topType
            ? `${topType.entity_type} ranks first by average opportunity score (${num(topType.avg_opp_score)}) across ${topType.opportunity_count} distinct entities in the selected scope.`
            : 'Expand the scope to surface opportunity type patterns.'}
        />
      </div>

      {/* Opportunity Matrix — hidden: urgency/unmet not well-defined yet
      <div style={{ marginBottom: '32px' }}>
        <Panel loading={loading} title="Opportunity Matrix" subtitle="Urgency vs unmet demand — bubble size = volume (conversations), color = opportunity score">
          {urgencyUnmet.length ? (
            <Plot
              data={oppScatterData}
              layout={{
                xaxis: { title: 'Urgency Ratio', range: [0, 1], tickformat: '.0%' },
                yaxis: { title: 'Unmet Demand Ratio', range: [0, 1], tickformat: '.0%' },
                plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 80, b: 50, l: 60 }, height: 380, showlegend: false,
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
              style={{ width: '100%' }}
            />
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No opportunity data available</div>}
        </Panel>
      </div>
      */}

      {/* Opportunity Daily Trend + Type Mix */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel loading={loading} title="Top Opportunities — Last 7 Days" subtitle="Daily conversation count per opportunity">
          {opportunityDaily.length ? (() => {
            const names = [...new Set(opportunityDaily.map(r => r.entity_name))];
            const colors = ['#1f5eff', '#1f7a3f', '#c98b12', '#c2410c', '#7c3aed', '#0f766e', '#0891b2', '#be185d', '#4338ca', '#65a30d'];
            const traces = names.map((name, i) => {
              const rows = opportunityDaily.filter(r => r.entity_name === name);
              return {
                x: rows.map(r => r.day),
                y: rows.map(r => r.conversation_count),
                type: 'scatter', mode: 'lines+markers', name,
                line: { color: colors[i % colors.length], width: 2 },
                marker: { size: 6 },
              };
            });
            return (
              <Plot
                data={traces}
                layout={{
                  xaxis: { title: 'Date', type: 'category' },
                  yaxis: { title: 'Conversations', dtick: 1 },
                  plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                  margin: { t: 20, r: 20, b: 50, l: 50 }, height: 300,
                  legend: { orientation: 'h', y: -0.3, x: 0.5, xanchor: 'center' },
                }}
                config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
                style={{ width: '100%' }}
              />
            );
          })() : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>

        <Panel loading={loading} title="Opportunity Type Mix" subtitle="Entity type distribution by average score">
          {typeMix.length ? (
            <Plot
              data={[{
                labels: typeMix.map(t => t.entity_type),
                values: typeMix.map(t => t.opportunity_count),
                type: 'pie',
                marker: { colors: ['#1f5eff', '#1f7a3f', '#c98b12', '#c2410c', '#7c3aed', '#0f766e'] },
              }]}
              layout={{
                paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 40, l: 20 }, height: 300,
              }}
              config={{ responsive: true, displaylogo: false }}
              style={{ width: '100%' }}
            />
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>
      </div>

      {/* Competitor Presence + Opportunity Table */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel loading={loading} title="Competitor Presence" subtitle="Mention volume by competitor (counted once per conversation) — click to see mention contexts">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {competitorVolume.length ? competitorVolume.map(c => {
              const isExpanded = expandedCompetitor === c.competitor_name;
              const mentions = competitorMentions[c.competitor_name];
              const isLoading = competitorMentionsLoading[c.competitor_name];
              return (
                <div key={c.competitor_name}>
                  <div
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedCompetitor(null);
                        return;
                      }
                      setExpandedCompetitor(c.competitor_name);
                      if (!mentions) {
                        setCompetitorMentionsLoading(prev => ({ ...prev, [c.competitor_name]: true }));
                        const params = new URLSearchParams();
                        Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
                        params.append('competitor', c.competitor_name);
                        axios.get(`/api/sales-strategy/competitor-mentions?${params}`)
                          .then(res => setCompetitorMentions(prev => ({ ...prev, [c.competitor_name]: res.data.data || [] })))
                          .catch(() => setCompetitorMentions(prev => ({ ...prev, [c.competitor_name]: [] })))
                          .finally(() => setCompetitorMentionsLoading(prev => ({ ...prev, [c.competitor_name]: false })));
                      }
                    }}
                    style={{
                      background: 'var(--bg-dark)', border: `1px solid ${isExpanded ? '#1e3a5f' : 'var(--card-border)'}`,
                      borderRadius: isExpanded ? '14px 14px 0 0' : '14px', padding: '12px 16px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
                      cursor: 'pointer', transition: 'border-color 0.2s',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '14px' }}>
                      {isExpanded ? '▼' : '▶'} {c.competitor_name}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>{c.mention_count} mentions</span>
                  </div>
                  {isExpanded && (
                    <div style={{
                      border: '1px solid #1e3a5f', borderTop: 'none',
                      borderRadius: '0 0 14px 14px', padding: '12px 16px',
                      background: '#f8f9fa', maxHeight: '300px', overflowY: 'auto',
                    }}>
                      {isLoading && <WaveLoader size="small" />}
                      {!isLoading && mentions && mentions.length === 0 && (
                        <div style={{ color: '#6c757d', fontSize: '13px' }}>No mention text available.</div>
                      )}
                      {!isLoading && mentions && mentions.length > 0 && (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase' }}>Context</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mentions.map((m, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                <td style={{ padding: '8px', color: '#6c757d', whiteSpace: 'nowrap' }}>{m.call_date ? new Date(m.call_date).toLocaleDateString() : '–'}</td>
                                <td style={{ padding: '8px' }}>{(() => {
                                  const raw = m.full_sentence || '';
                                  if (raw.includes('competitor_context=')) {
                                    const match = raw.match(/competitor_context=([^|]*)/);
                                    return match ? match[1].trim() : raw;
                                  }
                                  return raw || '–';
                                })()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div style={{ padding: '32px', textAlign: 'center', fontSize: '13px', color: 'var(--text-lighter)', border: '1.5px dashed var(--card-border)', borderRadius: '14px' }}>
                No competitor entities detected
              </div>
            )}
          </div>
        </Panel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
        <Panel loading={loading} title="Opportunities" subtitle="Ranked by unique conversations — click to see mention contexts">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {opportunities.length ? opportunities.slice(0, 12).map(o => {
              const isExpanded = expandedOpportunity === o.entity_name;
              const mentions = opportunityMentions[o.entity_name];
              const isLoading = opportunityMentionsLoading[o.entity_name];
              return (
                <div key={o.entity_name}>
                  <div
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedOpportunity(null);
                        return;
                      }
                      setExpandedOpportunity(o.entity_name);
                      if (!mentions) {
                        setOpportunityMentionsLoading(prev => ({ ...prev, [o.entity_name]: true }));
                        const params = new URLSearchParams();
                        Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
                        params.append('entity', o.entity_name);
                        axios.get(`/api/sales-strategy/opportunity-mentions?${params}`)
                          .then(res => setOpportunityMentions(prev => ({ ...prev, [o.entity_name]: res.data.data || [] })))
                          .catch(() => setOpportunityMentions(prev => ({ ...prev, [o.entity_name]: [] })))
                          .finally(() => setOpportunityMentionsLoading(prev => ({ ...prev, [o.entity_name]: false })));
                      }
                    }}
                    style={{
                      background: 'var(--bg-dark)', border: `1px solid ${isExpanded ? '#1e3a5f' : 'var(--card-border)'}`,
                      borderRadius: isExpanded ? '14px 14px 0 0' : '14px', padding: '12px 16px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
                      cursor: 'pointer', transition: 'border-color 0.2s',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '14px' }}>
                      {isExpanded ? '▼' : '▶'} {o.entity_name}
                      <span style={{ fontWeight: 400, color: 'var(--text-light)', fontSize: '12px', marginLeft: '8px' }}>{o.entity_type}</span>
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>{o.conversation_count} conversations</span>
                  </div>
                  {isExpanded && (
                    <div style={{
                      border: '1px solid #1e3a5f', borderTop: 'none',
                      borderRadius: '0 0 14px 14px', padding: '12px 16px',
                      background: '#f8f9fa', maxHeight: '300px', overflowY: 'auto',
                    }}>
                      {isLoading && <WaveLoader size="small" />}
                      {!isLoading && mentions && mentions.length === 0 && (
                        <div style={{ color: '#6c757d', fontSize: '13px' }}>No mention text available.</div>
                      )}
                      {!isLoading && mentions && mentions.length > 0 && (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: '11px', textTransform: 'uppercase' }}>Context</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mentions.map((m, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                <td style={{ padding: '8px', color: '#6c757d', whiteSpace: 'nowrap' }}>{m.call_date ? new Date(m.call_date).toLocaleDateString() : '–'}</td>
                                <td style={{ padding: '8px' }}>{m.full_sentence || '–'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div style={{ padding: '32px', textAlign: 'center', fontSize: '13px', color: 'var(--text-lighter)', border: '1.5px dashed var(--card-border)', borderRadius: '14px' }}>
                No opportunity data available
              </div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

export default FieldStrategy;
